import type { TransportTypesType } from "@/features/BuildingProject";

/**
 * A read of a project's transport rows plus the pointer naming its Default row.
 *
 * Structural rather than `GetProjectQuery`, because two reads produce this pair from the same two
 * tables and must be classified by the same rule: the working-state read (`GetProject`'s root
 * fields, which is assignable to this) and a revision's own rows through
 * `projectRevision.transports` / `transportDefaults`, which the revisions store maps for a preview.
 */
export interface TransportRowsRead {
  project_transport?: ReadonlyArray<TransportTypesType> | null;
  project_transport_default?: ReadonlyArray<{ project_transport_id: string }> | null;
}

/**
 * The project's transport rows as the wizard edits them, from the `GetProject` read.
 *
 * Two things happen on the way in. `transport.isDefault` is set from `project_transport_default` — the
 * row the pointer names is the project's Default row, every other row is generic — because that is what
 * every reader of the flag means by it: `Transport.vue` locks the Default row and lets generic rows be
 * removed, `insertProjectTransport` writes the pointer for the Default row only, and the mapping forms
 * pick "the default type" from it. Hasura's `transport.isDefault` says something else: that the row's
 * OPTION is the country's default option, which a generic row may use too (Ireland has two of them).
 * Read as the row's flag, a generic row using the default option showed up locked as a second Default
 * and, on the next save, took the pointer with it.
 *
 * And the rows are copied. The steps edit them in place (`TransportRow` writes the chosen option into
 * `item.transport`), which must never reach the objects Apollo handed out — the previewed revision's
 * rows least of all, since they live in the revisions store beside every other reader of them.
 */
export const toTransportTypes = (data: TransportRowsRead | null | undefined): TransportTypesType[] => {
  const defaultRowIds = new Set((data?.project_transport_default ?? []).map((row) => row.project_transport_id));

  return (data?.project_transport ?? []).map((row) => {
    const copy = JSON.parse(JSON.stringify(row)) as TransportTypesType;
    copy.transport.isDefault = defaultRowIds.has(row.id);
    return copy;
  });
};

export default toTransportTypes;
